import { designTokens, RiMapPinLine } from "@itandi/itandi-bb-ui";
import { Badge } from "@itandi/itandi-bb-ui/Badge";
import { Box } from "@itandi/itandi-bb-ui/Box";
import { Breadcrumbs } from "@itandi/itandi-bb-ui/Breadcrumbs";
import { Button } from "@itandi/itandi-bb-ui/Button";
import { Flex } from "@itandi/itandi-bb-ui/Flex";
import { TextLink } from "@itandi/itandi-bb-ui/TextLink";
import cx from "clsx";
import Link from "next/link";
import type { ComponentProps } from "react";
import { useMemo } from "react";
import { SimpleHeaderClientSP } from "~/components/common/layouts/deals/simple_header/SimpleHeaderClientSP";
import { DealTypeLabel } from "~/components/common/parts/label/DealTypeLabel";
import { MEDIA_QUERY, useMediaQuery } from "~/hooks/useMediaQuery";
import type { Deal } from "~/models/Deal";
import { formatPropertyName } from "~/models/Deal";
import type { Message } from "~/models/Message";
import { getUnreadCount } from "~/models/Message";
import { PATH as RELATED_DEAL_NEW_PATH } from "~/pages/deals/[id]/related_deals/new";
import { PATH as PROPERTY_DEAL_PATH } from "~/pages/property_deals/[id]";
import Styles from "./DealLayoutHeader.module.scss";

type Props = {
	deal: Deal;
	enableChat: boolean;
	isChatOpen: boolean;
	items: ComponentProps<
		typeof Breadcrumbs | typeof SimpleHeaderClientSP
	>["items"];
	messages: Message[];
	onToggleChat: () => void;
	useSimpleSpHeader: boolean;
};

export function DealLayoutHeaderClient({
	deal,
	enableChat,
	isChatOpen,
	items,
	messages,
	onToggleChat,
	useSimpleSpHeader
}: Props) {
	const { match: isSp } = useMediaQuery(MEDIA_QUERY.sp);
	const { match: isSingleColumn } = useMediaQuery(MEDIA_QUERY.singleColumn);
	const handleAddRelatedDealClick = (): void => {
		const href = RELATED_DEAL_NEW_PATH(deal.id);
		window.open(href, "_blank");
	};

	const unreadMessageCount = useMemo((): number => {
		return getUnreadCount(messages, "maintenance_company");
	}, [messages]);

	return (
		<>
			{useSimpleSpHeader && isSp ? (
				<SimpleHeaderClientSP deal={deal} items={items} />
			) : (
				<div className={Styles.DealHeader} id="deal_header">
					<div className={Styles.HeaderLeftContent}>
						<Box marginBottom={16}>
							<Breadcrumbs items={items} />
						</Box>
						<div className={Styles.HeaderElements}>
							<div className={Styles.AlignCenter}>
								<DealTypeLabel
									dealSubtype={deal.dealSubtype}
									dealType={deal.dealType}
								/>
							</div>
							<div>
								<h1 className={Styles.RoomName}>{formatPropertyName(deal)}</h1>
							</div>
							<div className={cx(Styles.AlignCenter, Styles.DealAddress)}>
								<RiMapPinLine className={Styles.Icon} />
								{deal.address}
							</div>
						</div>
					</div>
					<Flex
						alignItems="flex-end"
						gap={designTokens["spacing/m"]}
						justifyContent="end"
						ml="auto"
					>
						{enableChat && !isSingleColumn ? (
							<Button
								endIcon={
									unreadMessageCount > 0 ? (
										<Badge count={unreadMessageCount} variant="unread" />
									) : undefined
								}
								height="small"
								onClick={onToggleChat}
								variant="secondary"
							>
								{isChatOpen
									? "チャットを閉じる"
									: `施工会社とチャット${deal.messageSummaryUnread?.displayUnread && unreadMessageCount === 0 ? `（未読あり）` : ""}`}
							</Button>
						) : null}
						<Box pb={designTokens["spacing/xxs"]}>
							<TextLink
								as={Link}
								href={PROPERTY_DEAL_PATH(deal.propertyDealId)}
							>
								{deal.relatedDeals.length === 1
									? "関連する案件一覧"
									: `他${deal.relatedDeals.length - 1}件の関連する案件`}
							</TextLink>
						</Box>
						<Button height="small" onClick={handleAddRelatedDealClick}>
							関連する案件追加
						</Button>
					</Flex>
				</div>
			)}
		</>
	);
}
